import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'app/features/user-management/services/users.service';
import { SiteTypes } from 'app/shared/enums';
import { FeatureFlags } from 'app/shared/services/feature-flags/feature-flags.const';
import { NavigationItem, NavigationSection } from 'app/shared/types';
import { AuthorizationService } from 'core/services/auth/authorization.service';
import { ContextService } from 'core/services/context.service';
import { BehaviorSubject, combineLatest, filter, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Role } from 'shared/constants/role';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  // Base permissions available in all site types
  private baseMenuPermissions = [
    MenuIdentifiers.SUPPORT,
    MenuIdentifiers.USER_MANAGEMENT,
    MenuIdentifiers.SITE_SETTINGS,
    MenuIdentifiers.YARD_OPERATIONS_Tasks_Driver,
    MenuIdentifiers.YARD_OPERATIONS_Tasks_BackOffice,
    MenuIdentifiers.YARD_OPERATIONS_Tasks,
    MenuIdentifiers.YARD_OPERATIONS,
    MenuIdentifiers.SETTINGS,
  ];

  // Permissions for Essentials tier and above
  private essentialsAndAboveMenuPermissions = [
    MenuIdentifiers.YARD_CONTROL_ARRIVALS,
    MenuIdentifiers.YARD_CONTROL_YARD_AUDIT,
    MenuIdentifiers.YARD_CONTROL,
  ];

  // Permissions for Advanced tier and above
  private advancedAndAboveMenuPermissions = [
    MenuIdentifiers.YARD_OPERATIONS_DRIVER_DOCK_GROUPS,
    MenuIdentifiers.YARD_OPERATIONS_DOCK_ACTIVITY,
    MenuIdentifiers.YARD_OPERATIONS_SITE_MAP,
    MenuIdentifiers.YARD_OPERATIONS_MESSAGING,
    MenuIdentifiers.YARD_OPERATIONS_DRIVER_PERFORMANCE,
  ];

  private tierPermissions = {
    [SiteTypes.Unmanaged]: [...this.baseMenuPermissions],
    [SiteTypes.Essentials]: [...this.baseMenuPermissions, ...this.essentialsAndAboveMenuPermissions],
    [SiteTypes.Advanced]: [
      ...this.baseMenuPermissions,
      ...this.essentialsAndAboveMenuPermissions,
      ...this.advancedAndAboveMenuPermissions,
    ],
    [SiteTypes.Enterprise]: [
      ...this.baseMenuPermissions,
      ...this.essentialsAndAboveMenuPermissions,
      ...this.advancedAndAboveMenuPermissions,
    ],
  };
  public menuRoles = [
    {
      menu: MenuIdentifiers.SUPPORT,
      roleIds: [Role.SiteAdmin, Role.Driver, Role.TaskOffice, Role.BasicTaskOffice, Role.Gate, Role.DriverSupervisor],
    },
    { menu: MenuIdentifiers.SETTINGS, roleIds: [Role.SiteAdmin] },
    { menu: MenuIdentifiers.USER_MANAGEMENT, roleIds: [Role.SiteAdmin] },
    { menu: MenuIdentifiers.SITE_SETTINGS, roleIds: [Role.SiteAdmin] },
    {
      menu: MenuIdentifiers.YARD_CONTROL,
      roleIds: [
        Role.SiteAdmin,
        Role.TaskOffice,
        Role.BasicTaskOffice,
        Role.Gate,
        Role.Transportation,
        Role.Auditor,
        Role.DriverSupervisor,
      ],
    },
    {
      menu: MenuIdentifiers.YARD_CONTROL_YARD_AUDIT,
      roleIds: [Role.SiteAdmin, Role.Auditor],
    },
    {
      menu: MenuIdentifiers.YARD_CONTROL_ARRIVALS,
      roleIds: [
        Role.SiteAdmin,
        Role.TaskOffice,
        Role.BasicTaskOffice,
        Role.Gate,
        Role.Transportation,
        Role.DriverSupervisor,
      ],
    },
    {
      menu: MenuIdentifiers.YARD_CONTROL_PRODUCT_PROTECTION,
      roleIds: [],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS,
      roleIds: [
        Role.SiteAdmin,
        Role.Driver,
        Role.TaskOffice,
        Role.BasicTaskOffice,
        Role.Transportation,
        Role.DriverSupervisor,
        Role.Gate,
      ],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_SITE_MAP,
      roleIds: [
        Role.SiteAdmin,
        Role.Driver,
        Role.TaskOffice,
        Role.BasicTaskOffice,
        Role.Transportation,
        Role.DriverSupervisor,
        Role.Auditor,
        Role.Gate,
      ],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_DOCK_ACTIVITY,
      roleIds: [Role.SiteAdmin, Role.TaskOffice, Role.BasicTaskOffice, Role.DriverSupervisor],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_DRIVER_DOCK_GROUPS,
      roleIds: [Role.DriverSupervisor],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_Tasks,
      roleIds: [
        Role.SiteAdmin,
        Role.Driver,
        Role.TaskOffice,
        Role.BasicTaskOffice,
        Role.Transportation,
        Role.DriverSupervisor,
      ],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_Tasks_BackOffice,
      roleIds: [Role.SiteAdmin, Role.TaskOffice, Role.BasicTaskOffice, Role.Transportation, Role.DriverSupervisor],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_Tasks_Driver,
      roleIds: [Role.SiteAdmin, Role.Driver, Role.DriverSupervisor],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_MESSAGING,
      roleIds: [
        Role.SiteAdmin,
        Role.TaskOffice,
        Role.BasicTaskOffice,
        Role.Gate,
        Role.Auditor,
        Role.DriverSupervisor,
        Role.Driver,
      ],
    },
    {
      menu: MenuIdentifiers.YARD_OPERATIONS_DRIVER_PERFORMANCE,
      roleIds: [Role.DriverSupervisor],
    },
  ];
  protected readonly contextService = inject(ContextService);
  protected readonly translation = inject(TranslateService);
  protected readonly userService = inject(UsersService);
  protected readonly authorizationService = inject(AuthorizationService);

  private navigationReadySubject = new BehaviorSubject<boolean>(false);
  private gateServiceEnabled = false;

  constructor() {
    {
      combineLatest([
        this.contextService.siteId.pipe(startWith(null)),
        this.userService.getLoggedInUserWithRoles().pipe(filter((userInfo) => !!userInfo)),
      ])
        .pipe(map(() => true))
        .subscribe(() => {
          this.navigationReadySubject.next(true);
        });

      this.contextService.siteWithCapabilities$
        .pipe(map((site) => !!site.capabilities?.allowNexusGate))
        .subscribe((isEnabled) => {
          this.gateServiceEnabled = isEnabled;
        });
    }
  }

  public navigationReady$: Observable<boolean> = combineLatest([
    this.contextService.siteId.pipe(startWith(null)),
    this.userService.getLoggedInUserWithRoles().pipe(filter((userInfo) => !!userInfo)),
  ]).pipe(map(() => true));

  public navigationItems$: Observable<NavigationSection[]> = combineLatest([
    this.navigationReady$,
    this.contextService.siteWithCapabilities$.pipe(map((site) => !!site.capabilities?.allowNexusGate)),
  ]).pipe(map(() => this.getNavigationItems()));

  public getNavigationItems(): NavigationSection[] {
    const navigationSection: NavigationSection[] = [];
    navigationSection.push(...this.populateYardOperationsMenu());
    navigationSection.push(...this.populateYardControlMenu());
    navigationSection.push(...this.populateSettingsMenu());
    navigationSection.push(...this.populateGeneralMenu());
    return navigationSection;
  }

  public isMenuVisible(menu: string | undefined): boolean {
    if (this.authorizationService.isSuperAdmin()) return true;
    //TODO this default needs to be revisited and should be removed after function app release
    const siteTypeId = this.contextService.site.value?.siteType ?? SiteTypes.Enterprise;
    if (!siteTypeId) return false;
    const allowedMenuIdentifiers = this.getAllowedRolesForTier(siteTypeId);

    const filteredMenuRoles = this.menuRoles.filter((menuRole) => allowedMenuIdentifiers.includes(menuRole.menu));
    const menuItem = filteredMenuRoles.find((menuRole) => menuRole.menu.toLowerCase() === menu?.toLowerCase());

    if (!menuItem) {
      return false;
    }
    const hasAccessToMenu = this.authorizationService.checkSiteRoles(menuItem.roleIds);
    return hasAccessToMenu;
  }

  private populateYardControlMenu(): NavigationSection[] {
    if (this.gateServiceEnabled) {
      return [
        {
          label: this.translation.instant('navigation.yardControl.label'),
          toggle: false,
          divider: true,
          identifier: MenuIdentifiers.YARD_CONTROL,
          canView: (menu) => this.isMenuVisible(menu),
          items: [
            {
              label: this.translation.instant('navigation.yardControl.items.arrivals'),
              description: this.translation.instant('navigation.yardControl.items.arrivalsDescription'),
              identifier: MenuIdentifiers.YARD_CONTROL_ARRIVALS,
              routing: '/yard-control/inventory/',
              icon: 'local_shipping',
              canView: (menu) => this.isMenuVisible(menu),
            },
            // remove for mvp
            // {
            //   label: this.translation.instant('navigation.yardControl.items.productProtection'),
            //   description: this.translation.instant('navigation.yardControl.items.productProtectionDescription'),
            //   identifier: MenuIdentifiers.YARD_CONTROL_PRODUCT_PROTECTION,
            //   routing: null,
            //   icon: 'device_thermostat',
            //   canView: (menu) => this.isMenuVisible(menu),
            // },
            {
              label: this.translation.instant('navigation.yardControl.items.gateService'),
              description: this.translation.instant('navigation.yardControl.items.gateServiceDescription'),
              routing: '/yard-control/gate',
              icon: 'local_shipping',
              identifier: MenuIdentifiers.YARD_CONTROL_GATE_SERVICE,
              canView: (menu) => this.isMenuVisible(menu),
            },
            {
              label: this.translation.instant('navigation.yardControl.items.yardAudit'),
              description: this.translation.instant('navigation.yardControl.items.yardAuditDescription'),
              routing: '/yard-control/audit',
              icon: 'content_paste_search',
              identifier: MenuIdentifiers.YARD_CONTROL_YARD_AUDIT,
              canView: (menu) => this.isMenuVisible(menu),
            },
          ],
        },
      ];
    } else {
      return [
        {
          label: this.translation.instant('navigation.yardControl.label'),
          toggle: false,
          divider: true,
          identifier: MenuIdentifiers.YARD_CONTROL,
          canView: (menu) => this.isMenuVisible(menu),
          items: [
            {
              label: this.translation.instant('navigation.yardControl.items.arrivals'),
              description: this.translation.instant('navigation.yardControl.items.arrivalsDescription'),
              identifier: MenuIdentifiers.YARD_CONTROL_ARRIVALS,
              routing: '/yard-control/inventory/',
              icon: 'local_shipping',
              canView: (menu) => this.isMenuVisible(menu),
            },
            // remove for mvp
            // {
            //   label: this.translation.instant('navigation.yardControl.items.productProtection'),
            //   description: this.translation.instant('navigation.yardControl.items.productProtectionDescription'),
            //   identifier: MenuIdentifiers.YARD_CONTROL_PRODUCT_PROTECTION,
            //   routing: null,
            //   icon: 'device_thermostat',
            //   canView: (menu) => this.isMenuVisible(menu),
            // },
            {
              label: this.translation.instant('navigation.yardControl.items.yardAudit'),
              description: this.translation.instant('navigation.yardControl.items.yardAuditDescription'),
              routing: '/yard-control/audit',
              icon: 'content_paste_search',
              identifier: MenuIdentifiers.YARD_CONTROL_YARD_AUDIT,
              canView: (menu) => this.isMenuVisible(menu),
            },
          ],
        },
      ];
    }
  }

  private populateSettingsMenu(): NavigationSection[] {
    const navigationSection: NavigationSection = {
      label: this.translation.instant('navigation.settings.label'),
      toggle: false,
      divider: true,
      identifier: MenuIdentifiers.SETTINGS,
      canView: (menu) => this.isMenuVisible(menu),
      items: [],
    };

    navigationSection.items!.push({
      label: this.translation.instant('navigation.settings.items.userManagement'),
      description: this.translation.instant('navigation.settings.items.userManagementDescription'),
      routing: '/user-management',
      icon: 'people_outline',
      identifier: MenuIdentifiers.USER_MANAGEMENT,
      canView: (menu) => this.isMenuVisible(menu),
    });
    navigationSection.items!.push({
      label: this.translation.instant('navigation.settings.items.siteSettings'),
      description: this.translation.instant('navigation.settings.items.siteSettingsDescription'),
      routing: '/sites',
      icon: 'place',
      identifier: MenuIdentifiers.SITE_SETTINGS,
      canView: (menu) => this.isMenuVisible(menu),
    });
    return [navigationSection];
  }

  private populateGeneralMenu(): NavigationSection[] {
    return [
      {
        label: '',
        toggle: true,
        divider: false,
        canView: (menu) => this.isMenuVisible(menu),
        identifier: MenuIdentifiers.SUPPORT,
        items: [
          {
            label: this.translation.instant('navigation.support.label'),
            description: this.translation.instant('navigation.support.supportDescription'),
            routing: '/support',
            icon: 'contact_support',
            identifier: MenuIdentifiers.SUPPORT,
            canView: (menu) => this.isMenuVisible(menu),
          },
        ],
      },
    ];
  }

  private populateYardOperationsMenu(): NavigationSection[] {
    const isSuperAdmin = this.authorizationService.isSuperAdmin();
    const hasDriverRole = this.authorizationService.checkSiteRoles([Role.Driver]);
    const hasOfficeRole = this.authorizationService.checkSiteRoles([Role.TaskOffice, Role.BasicTaskOffice]);
    const hasTransportationOfficeRole = this.authorizationService.checkSiteRoles([Role.Transportation]);
    const hasDriverSupervisorRole = this.authorizationService.checkSiteRoles([Role.DriverSupervisor]);
    const isSiteAdmin = this.authorizationService.checkSiteRoles([Role.SiteAdmin]);
    const canViewAllTasks =
      (hasDriverRole && hasOfficeRole) ||
      (hasDriverRole && hasDriverSupervisorRole) ||
      isSiteAdmin ||
      isSuperAdmin ||
      (hasTransportationOfficeRole && hasDriverRole);

    const tasksItem: NavigationItem = {
      label: this.translation.instant('navigation.yardOperations.items.tasks'),
      routing: null,
      icon: 'task',
      identifier: MenuIdentifiers.YARD_OPERATIONS_Tasks,
      canView: (menu) => this.isMenuVisible(menu),
      subItems: [],
    };

    if (!tasksItem.subItems) {
      tasksItem.subItems = [];
    }

    if (canViewAllTasks) {
      tasksItem.subItems.push(
        {
          label: this.translation.instant('navigation.yardOperations.items.tasksSubItems.taskOffice'),
          description: this.translation.instant('navigation.yardOperations.items.tasksSubItems.taskOfficeDescription'),
          routing: '/yard-operations/tasks',
          icon: 'task',
          identifier: MenuIdentifiers.YARD_OPERATIONS_Tasks_BackOffice,
          canView: (menu) => this.isMenuVisible(menu),
        },
        {
          label: this.translation.instant('navigation.yardOperations.items.tasksSubItems.driver'),
          description: this.translation.instant('navigation.yardOperations.items.tasksSubItems.driverDescription'),
          routing: '/driver/yard-operations/tasks',
          customIcon: 'driver-task',
          identifier: MenuIdentifiers.YARD_OPERATIONS_Tasks_Driver,
          canView: (menu) => this.isMenuVisible(menu),
        },
      );
    } else if (hasDriverRole) {
      tasksItem.routing = '/driver/yard-operations/tasks';
      tasksItem.description = this.translation.instant(
        'navigation.yardOperations.items.tasksSubItems.driverDescription',
      );
    } else if (hasOfficeRole) {
      tasksItem.routing = '/yard-operations/tasks';
      tasksItem.description = this.translation.instant(
        'navigation.yardOperations.items.tasksSubItems.taskOfficeDescription',
      );
    } else if (hasTransportationOfficeRole || hasDriverSupervisorRole) {
      tasksItem.routing = '/yard-operations/tasks';
      tasksItem.description = this.translation.instant(
        'navigation.yardOperations.items.tasksSubItems.transportationDriverSuperDescription',
      );
    }

    const driverPerformanceFlagEnabled = this.contextService.isFeatureFlagEnabled(FeatureFlags.DriverPerformance);

    const siteMapsflagEnabled = this.contextService.isFeatureFlagEnabled(FeatureFlags.SiteMaps);

    const driverDockGroupsEnabled = this.contextService.isFeatureFlagEnabled(FeatureFlags.DriverDockGroups);

    const messagesEnabled = this.contextService.isFeatureFlagEnabled(FeatureFlags.MessagesSideMenu);

    const messagesMenuItems = messagesEnabled
      ? [
          {
            label: this.translation.instant('navigation.yardOperations.items.messages'),
            description: this.translation.instant('navigation.yardOperations.items.messagesDescription'),
            routing: '/messages',
            icon: 'comment',
            identifier: MenuIdentifiers.YARD_OPERATIONS_MESSAGING,
            canView: (menu: string) => this.isMenuVisible(menu),
          },
        ]
      : [];

    // Default return if no conditions above are met
    interface AdditionalDockActivityItem extends NavigationItem {
      label: string;
      description: string;
      routing: string | null;
      icon: string;
      identifier: string;
      canView: (menu: string) => boolean;
    }

    const additionalItems: AdditionalDockActivityItem[] = [
      {
        label: this.translation.instant('navigation.yardOperations.items.dockActivity'),
        description: this.translation.instant('navigation.yardOperations.items.dockActivityDescription'),
        routing: '/yard-operations/dock-activity',
        icon: 'meeting_room',
        identifier: MenuIdentifiers.YARD_OPERATIONS_DOCK_ACTIVITY,
        canView: (menu: string) => this.isMenuVisible(menu),
      } as AdditionalDockActivityItem,
    ];
    if (driverPerformanceFlagEnabled) {
      additionalItems.push({
        label: this.translation.instant('navigation.yardOperations.items.driverPerformance'),
        description: this.translation.instant('navigation.yardOperations.items.driverPerformanceDescription'),
        routing: '/yard-operations/driver-performance',
        icon: 'trending_up',
        identifier: 'yardOperationsDriverPerformance',
        canView: (menu) => this.isMenuVisible(menu),
      });
    }

    if (siteMapsflagEnabled) {
      additionalItems.push({
        label: this.translation.instant('navigation.yardOperations.items.siteMap'),
        description: this.translation.instant('navigation.yardOperations.items.siteMapDescription'),
        routing: null,
        icon: 'map',
        identifier: MenuIdentifiers.YARD_OPERATIONS_SITE_MAP,
        canView: (menu) => this.isMenuVisible(menu),
      });
    }
    if (driverDockGroupsEnabled) {
      additionalItems.push({
        label: this.translation.instant('navigation.yardOperations.items.driverDockGroups'),
        description: this.translation.instant('navigation.yardOperations.items.driverDockGroupsDescription'),
        routing: '/yard-operations/driver-dock-groups',
        icon: 'dashboard',
        identifier: MenuIdentifiers.YARD_OPERATIONS_DRIVER_DOCK_GROUPS,
        canView: (menu) => this.isMenuVisible(menu),
      });
    }

    return [
      {
        label: this.translation.instant('navigation.yardOperations.label'),
        toggle: false,
        divider: true,
        identifier: MenuIdentifiers.YARD_OPERATIONS,
        canView: (menu) => this.isMenuVisible(menu),
        items: [tasksItem, ...additionalItems, ...messagesMenuItems],
      },
    ];
  }

  public getAllowedRolesForTier(siteTypeId: string): (string | number)[] {
    switch (siteTypeId) {
      case SiteTypes.Unmanaged:
        return this.tierPermissions[SiteTypes.Unmanaged];
      case SiteTypes.Essentials:
        return this.tierPermissions[SiteTypes.Essentials];
      case SiteTypes.Advanced:
        return this.tierPermissions[SiteTypes.Advanced];
      case SiteTypes.Enterprise:
        return this.tierPermissions[SiteTypes.Enterprise];
      default:
        return [];
    }
  }
}

export class MenuIdentifiers {
  public static readonly YARD_CONTROL = 'yardControl';
  public static readonly YARD_CONTROL_ARRIVALS = 'yardControlArrivals';
  public static readonly YARD_CONTROL_PRODUCT_PROTECTION = 'yardControlProductProtection';
  public static readonly YARD_CONTROL_YARD_AUDIT = 'yardControlYardAudit';
  public static readonly SETTINGS = 'settings';
  public static readonly USER_MANAGEMENT = 'userManagement';
  public static readonly SITE_SETTINGS = 'siteSettings';
  public static readonly SUPPORT = 'support';
  public static readonly YARD_OPERATIONS = 'yardOperations';
  public static readonly YARD_OPERATIONS_Tasks = 'yardOperationsTasks';
  public static readonly YARD_OPERATIONS_Tasks_Driver = 'yardOperationsDriver';
  public static readonly YARD_OPERATIONS_Tasks_BackOffice = 'yardOperationsTaskOffice';
  public static readonly YARD_OPERATIONS_DOCK_ACTIVITY = 'yardOperationsDockActivity';
  public static readonly YARD_OPERATIONS_MESSAGES = 'yardOperationsMessages';
  public static readonly YARD_OPERATIONS_SITE_MAP = 'yardOperationsSiteMap';
  public static readonly YARD_OPERATIONS_DRIVER_DOCK_GROUPS = 'yardOperationsDriverDockGroups';
  public static readonly YARD_OPERATIONS_MESSAGING = 'yardOperationsMessaging';
  public static readonly YARD_OPERATIONS_DRIVER_PERFORMANCE = 'yardOperationsDriverPerformance';
  public static readonly YARD_CONTROL_GATE_SERVICE = 'gateService';
}
